import React, { useEffect, useMemo } from 'react';
import { Route, Routes, NavLink, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AppBar, Drawer, Toolbar, Typography, List, ListItem, ListItemText, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import HomePage from './Home';
import EditAppUser from './EditAppUser';
import Reports from './Reports';
import EditSystemUser from './EditSystemUser';
import CreateUserGroup from './user_groups/CreateUserGroup';
import UserGroupsPage from './user_groups/UserGroupsPage';
import Masters from './masters/Masters';
import SchemesList from './schemes/SchemesList';
import AppUsersTable from './AppUsers';
import SystemUsers from './SystemUsers';
import AuditLogs from './AuditLogs';
import Advertisement from './Advertisement';
import YojanaApprovals from './YojanaApprovals';
import MyYojanaRequests from './MyYojanaRequests';
import LogoutIcon from '@mui/icons-material/Logout';
import ProtectedRoute from '../components/ProtectedRoute';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../store/slices/authSlice';
import { usePermissions } from '../hooks/usePermissions';

const drawerWidth = 240;

const Root = styled('div')({
  display: 'flex',
});

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0 16px',
}));

const StyledDrawer = styled(Drawer)({
  width: drawerWidth,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: drawerWidth,
  },
});

const MainContent = styled('main')(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
}));

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector(selectCurrentUser);
  const { canViewTab } = usePermissions();

  const storedGroupName =
    user?.userGroup?.groupName ||
    (typeof window !== 'undefined' ? localStorage.getItem('userGroupName') : '') ||
    '';

  const isSuperAdmin = useMemo(() => {
    const normalizedGroupName = storedGroupName
      ?.toLowerCase?.()
      ?.replace(/[\s_-]+/g, '') || '';
    const normalizedEmployeeCode = user?.employeeCode
      ?.toLowerCase?.()
      ?.replace(/[\s_-]+/g, '') || '';
    return normalizedGroupName === 'superadmin' || normalizedEmployeeCode === 'superadmin';
  }, [storedGroupName, user?.employeeCode]);

  useEffect(() => {
    // Check for token on component mount
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login', { state: { from: location } });
    }
  }, [navigate, location]);

  const handleLogout = () => {
    // Clear all local storage data
    localStorage.clear();
    // Redirect to login page
    navigate('/login');
  };

  return (
    <ProtectedRoute>
      <Root>
        {/* Top Navigation Bar */}
        <StyledAppBar position="fixed">
          <Typography variant="h6" noWrap>
            Admin Dashboard
          </Typography>
          <Button
            color="inherit"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{ 
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            Logout
          </Button>
        </StyledAppBar>

        {/* Sidebar Drawer */}
        <StyledDrawer variant="permanent">
          <Toolbar />
          <List>
            {[
              { text: 'Home', path: '/dashboard/home', tabKey: 'home', alwaysVisible: true },
              { text: 'App Users', path: '/dashboard/users', tabKey: 'appUsers' },
              { text: 'Schemes', path: '/dashboard/schemes', tabKey: 'schemes' },
              { text: 'Yojana Approvals', path: '/dashboard/yojana-approvals', tabKey: 'yojanaApprovals', superAdminOnly: true },
              { text: 'My Yojana Requests', path: '/dashboard/my-yojana-requests', tabKey: 'myYojanaRequests', nonSuperAdminOnly: true },
              { text: 'System Users', path: '/dashboard/system-users', tabKey: 'systemUsers' },
              { text: 'User Groups', path: '/dashboard/user-groups', tabKey: 'userGroups', superAdminOnly: true },
              { text: 'Masters', path: '/dashboard/masters', tabKey: 'masters' },
              { text: 'Reports', path: '/dashboard/reports', tabKey: 'reports' },
              { text: 'Advertisement', path: '/dashboard/ads', tabKey: 'advertisement', superAdminOnly: true },
              { text: 'Audit Logs', path: '/dashboard/audit-logs', tabKey: 'auditLogs', superAdminOnly: true },
            ]
              .filter(({ alwaysVisible, tabKey, superAdminOnly, nonSuperAdminOnly }) => {
                if (alwaysVisible) return true;
                if (superAdminOnly) return isSuperAdmin;
                if (nonSuperAdminOnly) return !isSuperAdmin && canViewTab('schemes');
                return canViewTab(tabKey);
              })
              .map(({ text, path }) => (
                <ListItem key={text} component={NavLink} to={path} button>
                  <ListItemText primary={text} />
                </ListItem>
              ))}
          </List>
        </StyledDrawer>

        {/* Main Content */}
        <MainContent>
          <Toolbar />
          <Routes>
            <Route path="/" element={<Navigate to="home" replace />} />
            <Route path="home" element={<HomePage/>} />
            <Route path="users" element={<AppUsersTable />} />
            <Route path="users/edit/:id" element={<EditAppUser />} />
            <Route path="schemes" element={<SchemesList />} />
            {isSuperAdmin && <Route path="yojana-approvals" element={<YojanaApprovals />} />}
            <Route path="my-yojana-requests" element={<MyYojanaRequests />} />
            <Route path="system-users" element={<SystemUsers />} />
            <Route path="system-users/edit/:id" element={<EditSystemUser />} />
            <Route path="user-groups" element={<UserGroupsPage />} />
            <Route path="user-groups/create" element={<CreateUserGroup />} />
            <Route path="masters/*" element={<Masters />} />
            <Route path="reports" element={<Reports />} />
            {isSuperAdmin && <Route path="ads" element={<Advertisement />} />}
            {isSuperAdmin && <Route path="audit-logs" element={<AuditLogs />} />}
            {/* Catch all route */}
            <Route path="*" element={<Navigate to="home" replace />} />
          </Routes>
        </MainContent>
      </Root>
    </ProtectedRoute>
  );
};

export default Dashboard;
